import React, { useState, useEffect } from 'react';
import { 
  HardDrive, Cloud, Server, CheckCircle2, XCircle, 
  Loader2, Eye, EyeOff, Save, TestTube
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_TYPES = [
  { id: 'local', label: 'Local', icon: HardDrive, desc: 'Stockage sur le serveur' },
  { id: 'wasabi', label: 'Wasabi S3', icon: Cloud, desc: 'Stockage cloud S3 compatible' },
  { id: 'sftp', label: 'SFTP', icon: Server, desc: 'Serveur SFTP distant' }
];

const StorageSettings = ({ token, toast }) => {
  const [storageType, setStorageType] = useState('local');
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageType(res.data.storage_type || 'local');
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading storage settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const payload = { ...config, storage_type: storageType };
      await axios.post(`${API}/admin/settings/storage`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Succès', description: 'Configuration du stockage enregistrée' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la sauvegarde',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { ...config, storage_type: storageType };
      const res = await axios.post(`${API}/admin/settings/storage/test`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(res.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.response?.data?.detail || 'Erreur de connexion'
      });
    } finally {
      setTesting(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setTestResult(null);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="storage-settings">
      {/* Storage Type Selection */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Type de stockage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STORAGE_TYPES.map(type => {
            const Icon = type.icon;
            const isActive = storageType === type.id;
            return (
              <button
                key={type.id}
                onClick={() => { setStorageType(type.id); setTestResult(null); }}
                data-testid={`storage-type-${type.id}`}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  isActive
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-blue-500/20 bg-slate-800/30 hover:border-blue-500/40 hover:bg-slate-800/50'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${isActive ? 'bg-blue-500/20' : 'bg-slate-700/50'}`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'text-blue-400' : 'text-blue-300/50'}`} />
                  </div>
                  <span className={`font-semibold ${isActive ? 'text-white' : 'text-blue-200/70'}`}>
                    {type.label}
                  </span>
                </div>
                <p className={`text-sm ${isActive ? 'text-blue-300/80' : 'text-blue-300/40'}`}>
                  {type.desc}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Wasabi S3 Configuration */}
      {storageType === 'wasabi' && (
        <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6 space-y-4" data-testid="wasabi-config">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-400" />
            Configuration Wasabi S3
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Access Key *</label>
              <input
                type="text"
                value={config.wasabi_access_key || ''}
                onChange={(e) => updateConfig('wasabi_access_key', e.target.value)}
                placeholder="AKIAIOSFODNN7EXAMPLE"
                data-testid="wasabi-access-key"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Secret Key *</label>
              <div className="relative">
                <input
                  type={showPasswords.wasabi_secret ? 'text' : 'password'}
                  value={config.wasabi_secret_key || ''}
                  onChange={(e) => updateConfig('wasabi_secret_key', e.target.value)}
                  placeholder="wJalrXUtnFEMI/K7MDENG/..."
                  data-testid="wasabi-secret-key"
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('wasabi_secret')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/50 hover:text-blue-300"
                >
                  {showPasswords.wasabi_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Bucket *</label>
              <input
                type="text"
                value={config.wasabi_bucket || ''}
                onChange={(e) => updateConfig('wasabi_bucket', e.target.value)}
                placeholder="sendbox-files"
                data-testid="wasabi-bucket"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Région</label>
              <select
                value={config.wasabi_region || 'eu-central-1'}
                onChange={(e) => updateConfig('wasabi_region', e.target.value)}
                data-testid="wasabi-region"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-400 text-sm cursor-pointer"
              >
                <option value="eu-central-1">EU Central 1 (Amsterdam)</option>
                <option value="eu-central-2">EU Central 2 (Francfort)</option>
                <option value="eu-west-1">EU West 1 (Londres)</option>
                <option value="eu-west-2">EU West 2 (Paris)</option>
                <option value="us-east-1">US East 1 (Virginie)</option>
                <option value="us-east-2">US East 2 (Virginie)</option>
                <option value="us-west-1">US West 1 (Oregon)</option>
                <option value="ap-northeast-1">AP Northeast 1 (Tokyo)</option>
                <option value="ap-southeast-1">AP Southeast 1 (Singapour)</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm text-blue-300/70 mb-1.5">Endpoint URL</label>
              <input
                type="text"
                value={config.wasabi_endpoint || 'https://s3.eu-central-1.wasabisys.com'}
                onChange={(e) => updateConfig('wasabi_endpoint', e.target.value)}
                placeholder="https://s3.eu-central-1.wasabisys.com"
                data-testid="wasabi-endpoint"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* SFTP Configuration */}
      {storageType === 'sftp' && (
        <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6 space-y-4" data-testid="sftp-config">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <Server className="w-5 h-5 text-green-400" />
            Configuration SFTP
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Hôte *</label>
              <input
                type="text"
                value={config.sftp_host || ''}
                onChange={(e) => updateConfig('sftp_host', e.target.value)}
                placeholder="sftp.example.com"
                data-testid="sftp-host"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Port</label>
              <input
                type="number"
                value={config.sftp_port || 22}
                onChange={(e) => updateConfig('sftp_port', parseInt(e.target.value) || 22)}
                placeholder="22"
                data-testid="sftp-port"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Nom d'utilisateur *</label>
              <input
                type="text"
                value={config.sftp_username || ''}
                onChange={(e) => updateConfig('sftp_username', e.target.value)}
                placeholder="sendbox"
                data-testid="sftp-username"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm text-blue-300/70 mb-1.5">Mot de passe *</label>
              <div className="relative">
                <input
                  type={showPasswords.sftp_password ? 'text' : 'password'}
                  value={config.sftp_password || ''}
                  onChange={(e) => updateConfig('sftp_password', e.target.value)}
                  placeholder="********"
                  data-testid="sftp-password"
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('sftp_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/50 hover:text-blue-300"
                >
                  {showPasswords.sftp_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm text-blue-300/70 mb-1.5">Répertoire distant</label>
              <input
                type="text"
                value={config.sftp_remote_dir || '/sendbox'}
                onChange={(e) => updateConfig('sftp_remote_dir', e.target.value)}
                placeholder="/sendbox"
                data-testid="sftp-remote-dir"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Local info */}
      {storageType === 'local' && (
        <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6" data-testid="local-config">
          <div className="flex items-start gap-3">
            <HardDrive className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <h4 className="text-white font-semibold">Stockage local</h4>
              <p className="text-sm text-blue-300/60 mt-1">
                Les fichiers sont stockés directement sur le serveur dans le répertoire 
                <code className="mx-1 px-1.5 py-0.5 bg-slate-700/50 rounded text-blue-300 text-xs">/uploads</code>.
                Adapté pour les petites installations ou le développement.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Test Result */}
      {testResult && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${
          testResult.success 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`} data-testid="test-result">
          {testResult.success ? (
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          )}
          <span className={`text-sm ${testResult.success ? 'text-green-300' : 'text-red-300'}`}>
            {testResult.message}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          onClick={handleTest}
          disabled={testing}
          variant="outline"
          className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
          data-testid="storage-test-btn"
        >
          {testing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <TestTube className="w-4 h-4 mr-2" />
          )}
          Tester la connexion
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="storage-save-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

export default StorageSettings;
